import {
  Box,
  Button,
  Dialog,
  Text,
  useResponsiveValue,
  IconShieldCheck,
  Stack,
  Bleed,
  Divider,
  Textarea,
  IconInfoInverted,
  Columns,
  Column,
  PopoverRenderer,
  TextLink,
  Checkbox
} from '@machobear/design-system'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { pipe, filter, includes, prop, isEmpty } from 'ramda'
import { useDropzone } from 'react-dropzone'
import { Dropdown, CurrencyField } from '@/components/ds-form'
import { DialogEventHandler } from '@/types'
import useProfile from '@/hooks/use-profile'
import useBasic from '@/hooks/use-basic'
import isTruthy from '@/utils/is-truthy'
import { AvatarPickerDS } from '@/components/avatar/avatar-picker-ds'
import useSessionStorage from '@/hooks/use-sessionstorage'

interface EmploymentType {
  txt_id: string
  name: string
}

export type EmploymentTypes = {
  [key: string]: EmploymentType
}

type DialogKeys = 'welcomeWizard' | 'introModal'
type Props = {
  visible: boolean
  showModal: (id: DialogKeys) => DialogEventHandler
  closeModal: (id: DialogKeys) => DialogEventHandler
  title: string
}

type formValues = {
  job_title?: string | null
  salary?: string | null
  about_me: string
  prepay: boolean | null
}

const EMPLOYMENT_TYPE = ['unemployed', 'self_employed', 'student']
const OTHER_TYPE = ['other']

const IntroModal = ({ visible, closeModal }: Props) => {
  const { t } = useTranslation()
  const data = useProfile()
  const dispatch = useDispatch()
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  const [, setDisplayIntro] = useSessionStorage('renter-intro')

  const formOptions = () => ({
    initialValues: {
      job_title: data?.job_title || '',
      salary: data?.salary && !isTruthy(data?.prepay) ? data?.salary : '',
      about_me: data?.about_me || '',
      prepay: isTruthy(data?.prepay) || false
    },
    validationSchema: Yup.object().shape({
      job_title: Yup.string(),
      salary: Yup.number().min(0),
      about_me: Yup.string().max(260, t('r.profile.edit.about_me_max.text', 'Max 260 characters')),
      prepay: Yup.bool()
    }),
    onSubmit: async (values: formValues, { setSubmitting }: any) => {
      const response = await dispatch.profile.saveWelcomeModal(values)
      setSubmitting(false)
      if (response.ok) {
        window?.dataLayer?.push({
          event: 'app_welcomemodal_standout_contapp'
        })
        closeModal('introModal')()
      }
    }
  })

  const { professions }: EmploymentTypes = useBasic(['professions'])
  const professionsArr: {
    value: any
    label: string
  }[] = Object.values(professions).map(profession => ({
    value: profession.txt_id,
    label: t(`basic:b.professions.view.${profession.txt_id}.text`)
  }))

  const employmentTypeOptions = pipe(filter(obj => includes(prop('value', obj), EMPLOYMENT_TYPE)))(
    professionsArr
  )
  const otherTypeOptions = pipe(filter(obj => includes(prop('value', obj), OTHER_TYPE)))(
    professionsArr
  )
  const professionTypeOptions: { value: string; label: string }[] = professionsArr
    .filter(obj => ![...EMPLOYMENT_TYPE, ...OTHER_TYPE].includes(obj.value))
    .sort((a, b) => a.value.localeCompare(b.value))

  const professionOptions = [
    ...employmentTypeOptions,
    ...professionTypeOptions,
    ...otherTypeOptions
  ]

  const [preview, setPreview] = useState<string | null>(null)
  const [avatarErrorMessage, setAvatarErrorMessage] = useState<string | null>(null)
  const profile = useProfile()
  const handleUploadAvatar = (file: any) => {
    dispatch.profile.uploadAvatar({
      file,
      fileId: profile?.avatar?.id
    })
  }
  const { getInputProps, getRootProps, open } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxSize: 10485760,
    multiple: false,
    noKeyboard: true,
    onDropAccepted: ([file]) => {
      setPreview(URL.createObjectURL(file))
      setAvatarErrorMessage(null)
      handleUploadAvatar(file)
    },
    onDropRejected: error => {
      setAvatarErrorMessage(error[0].errors[0].message)
    }
  })

  const handleClose = () => {
    closeModal('introModal')()
    setDisplayIntro?.('false')
  }

  return (
    <>
      <Dialog
        title={t('r.profile.view.intro_modal.title', 'Stand out from the crowd')}
        description={t(
          'r.profile.view.intro_modal.description',
          'Get discovered by landlords who have available rental listings - helping you find a home faster.'
        )}
        id="introModal"
        open={visible}
        onClose={handleClose}
        width="small"
      >
        <Stack space="large">
          <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
            <Divider />
          </Bleed>
          <Stack space="small">
            <Box display="flex" alignItems="center">
              <Box marginRight="xsmall">
                <AvatarPickerDS
                  getInputProps={getInputProps}
                  getRootProps={getRootProps}
                  preview={preview}
                />
              </Box>
              <Stack space="medium">
                <Box
                  cursor="pointer"
                  component="button"
                  maxWidth="medium"
                  tabIndex={0}
                  onClick={open}
                >
                  <Text tone="link">
                    {t('r.profile.view.upload_photo.text', 'Upload your photo')}
                  </Text>
                </Box>
                <Box>
                  <Text size="small">
                    {t(
                      'r.profile.view.file_description.text',
                      'Max file size: 10 mb. PNG & JPEG only.'
                    )}
                  </Text>
                </Box>
              </Stack>
            </Box>
            <Box>
              {avatarErrorMessage && (
                <Text size="xsmall" tone="critical">
                  {avatarErrorMessage}
                </Text>
              )}
            </Box>
          </Stack>
          <Formik validateOnMount {...formOptions()}>
            {({ values, errors, setFieldValue, setFieldTouched, touched, isSubmitting }) => {
              return (
                <Form>
                  <Stack space="large">
                    <Stack space="small">
                      <Text weight="strong">
                        {t('r.prfile.edit.your_story.title', 'What’s your story?')}
                      </Text>
                      <Textarea
                        name="about_me"
                        id="about_me"
                        label={t(
                          'r.profile.edit.your_story2.placeholder',
                          'Tell landlords more about you or share why you are moving.'
                        )}
                        value={values.about_me}
                        characterLimit={260}
                        onFocus={() => setFieldTouched('about_me', true)}
                        onChange={(e: any) => {
                          if (e.target.value.length <= 260)
                            setFieldValue('about_me', e.target.value)
                        }}
                        tone={errors.about_me && touched.about_me ? 'critical' : undefined}
                        message={errors.about_me && touched.about_me ? errors.about_me : undefined}
                        resize={false}
                      />
                    </Stack>

                    <Bleed top="medium">
                      <Stack space="small">
                        <Text weight="strong">
                          {t('r.prfile.edit.your_income.title', 'What’s your income?')}
                        </Text>
                        <Columns space="medium">
                          <Column>
                            <CurrencyField
                              disabled={values?.prepay}
                              name="salary"
                              label={t('b.profile.general.annual_income.text', 'Annual Income')}
                            />
                          </Column>
                          <Column>
                            <Box marginTop="small" display="flex" alignItems="center" width="full">
                              <Checkbox
                                id="prepay"
                                disabled={isTruthy(values?.salary)}
                                label={t(
                                  'r.profile.edit.can_prepay_rent.text',
                                  'I can prepay 12 months rent'
                                )}
                                onChange={(e: any) => {
                                  setFieldValue('salary', undefined)
                                  setFieldValue('prepay', e.target.checked)
                                }}
                                checked={values?.prepay}
                              />
                            </Box>
                          </Column>
                        </Columns>

                        <PopoverRenderer
                          trigger={({ triggerProps }) => (
                            <Box
                              {...triggerProps}
                              cursor="pointer"
                              display="flex"
                              alignItems="center"
                              gap="xxxsmall"
                              style={{ width: 'max-content' }}
                            >
                              <Text
                                size="small"
                                tone="info"
                                baseline={false}
                                icon={<IconInfoInverted />}
                              >
                                {t(
                                  'r.profile.view.intro_modal.tooltip.trigger',
                                  'Why do we need this?'
                                )}
                              </Text>
                            </Box>
                          )}
                        >
                          {() => (
                            <Box paddingBottom="xxxsmall">
                              <Stack space="small">
                                <Text size="small">
                                  {t(
                                    'r.profile.view.intro_modal.tooltip.content',
                                    'Although optional, this enhances your chances of being matched with landlords who are ready to engage in a conversation.'
                                  )}
                                </Text>
                                <Text weight="strong" size="small">
                                  <TextLink
                                    href="https://liv.rent/blog/livrent/suggested-tenants/"
                                    target="_blank"
                                  >
                                    {t('common.learn_more', 'Learn more')}
                                  </TextLink>
                                </Text>
                              </Stack>
                            </Box>
                          )}
                        </PopoverRenderer>
                      </Stack>
                    </Bleed>

                    <Stack space="small">
                      <Text weight="strong">
                        {t('r.prfile.edit.your_profession.title', 'What’s your profession?')}
                      </Text>

                      <Columns space="medium">
                        <Column width={isMobile ? undefined : '1/2'}>
                          <Dropdown
                            name="job_title"
                            label={t(
                              'r.profile.edit.welcome_modal_job_title.label',
                              'Select profession'
                            )}
                            options={professionOptions}
                          />
                        </Column>
                      </Columns>
                    </Stack>

                    <Box display="flex" alignItems="center" gap="xxxsmall">
                      <Text size="xsmall" icon={<IconShieldCheck tone="brandAccent" />}>
                        {t(
                          'r.profile.view.disclaimer.text',
                          'Your personal information is only used to facilitate applications. We never sell user data.'
                        )}
                      </Text>
                    </Box>

                    <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
                      <Divider />
                    </Bleed>
                    <Box
                      width="full"
                      display="flex"
                      justifyContent="flexEnd"
                      flexDirection={isMobile ? 'columnReverse' : 'row'}
                    >
                      <Box marginRight={isMobile ? 'none' : 'xsmall'}>
                        <Button
                          variant="transparent"
                          onClick={() => {
                            window?.dataLayer?.push({
                              event: 'app_welcomemodal_standout_skip'
                            })
                            handleClose()
                          }}
                        >
                          {t('r.profile.view.intro_skip.button', 'Skip for Now')}
                        </Button>
                      </Box>
                      <Box marginBottom={isMobile ? 'xsmall' : 'none'}>
                        <Button type="submit" disabled={!isEmpty(errors)} loading={isSubmitting}>
                          {isSubmitting
                            ? t('common.submitting2', 'Submitting')
                            : t('common.continue', 'Continue')}
                        </Button>
                      </Box>
                    </Box>
                  </Stack>
                </Form>
              )
            }}
          </Formik>
        </Stack>
      </Dialog>
    </>
  )
}

export default IntroModal
